import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import { useConfig } from '../context/ConfigContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import {
  Users, Activity, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  RefreshCw, Shield, Target, Zap, Clock, BarChart3, UserPlus, Flame,
  Loader2, AlertCircle, ChevronRight, Brain, Gauge, Heart, Calendar,
  ArrowUpRight, ArrowDownRight, Minus, Bell, Play, RotateCcw, Sliders,
  Filter, X, Download, FileText, FileSpreadsheet
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '../components/ModernUI';
import BurnoutAlerts from '../components/BurnoutAlerts';
import { EnhancedReportSection } from '../components/EnhancedReportSection';
import DataSyncStatus from '../components/DataSyncStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color utility functions
const getStatusColor = (color) => {
  const colors = {
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    yellow: 'bg-amber-100 text-amber-700 border-amber-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    gray: 'bg-slate-100 text-slate-700 border-slate-200'
  };
  return colors[color] || colors.gray;
};

const getStatusBg = (color) => {
  const colors = {
    green: 'bg-emerald-50 border-emerald-200',
    yellow: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
    gray: 'bg-slate-50 border-slate-200'
  };
  return colors[color] || colors.gray;
};

// Capacity icon component
const CapacityIcon = ({ status }) => {
  if (status === 'Critical Overcapacity') return <Flame className="h-4 w-4 text-red-600" />;
  if (status === 'At Capacity') return <AlertTriangle className="h-4 w-4 text-red-500" />;
  if (status === 'Approaching Capacity') return <AlertCircle className="h-4 w-4 text-amber-500" />;
  return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
};

export default function WorkforceIntelligencePage() {
  const { token } = useAuth();
  const { getWorkforceTarget, getRNCMCaseloadTarget } = useConfig();
  
  // Get configurable targets from context
  const workforceTargets = useMemo(() => ({
    staffUtilization: getWorkforceTarget('staff_utilization_target', 0.85),
    turnoverRate: getWorkforceTarget('turnover_rate_target', 0.10),
    burnoutRate: getWorkforceTarget('burnout_rate_target', 0.05),
    prnUtilization: getWorkforceTarget('prn_utilization_target', 0.10),
    caseloadTarget: getRNCMCaseloadTarget()
  }), [getWorkforceTarget, getRNCMCaseloadTarget]);
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Scenario Simulator State
  const [simulatorLoading, setSimulatorLoading] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState('all'); // Team filter state
  const [scenarioInputs, setScenarioInputs] = useState({
    adc_growth_pct: 0,
    staff_change: 0,
    pto_days_next_month: 0,
    avg_visits_per_patient: 2.5,
    new_hire_ramp_weeks: 4
  });

  useEffect(() => {
    fetchDashboardData();
    fetchAlerts();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/workforce/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      } else {
        toast.error('Failed to load workforce data');
      }
    } catch (error) {
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/workforce/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const runSimulation = async () => {
    setSimulatorLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/workforce/simulate/advanced`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(scenarioInputs)
      });
      if (res.ok) {
        const data = await res.json();
        setSimulationResult(data);
        toast.success('Simulation complete!');
      } else {
        toast.error('Failed to run simulation');
      }
    } catch (error) {
      toast.error('Error running simulation');
    } finally {
      setSimulatorLoading(false);
    }
  };

  const resetSimulation = () => {
    setScenarioInputs({
      adc_growth_pct: 0,
      staff_change: 0,
      pto_days_next_month: 0,
      avg_visits_per_patient: 2.5,
      new_hire_ramp_weeks: 4
    });
    setSimulationResult(null);
  };

  // Scenario Presets
  const scenarioPresets = [
    {
      id: 'growth',
      name: 'Growth Mode',
      icon: TrendingUp,
      color: 'emerald',
      description: 'High census growth with new hires',
      values: { adc_growth_pct: 15, staff_change: 2, pto_days_next_month: 5, avg_visits_per_patient: 2.5, new_hire_ramp_weeks: 4 }
    },
    {
      id: 'shortage',
      name: 'Staff Shortage',
      icon: AlertTriangle,
      color: 'red',
      description: 'Losing staff with steady census',
      values: { adc_growth_pct: 0, staff_change: -2, pto_days_next_month: 0, avg_visits_per_patient: 2.5, new_hire_ramp_weeks: 4 }
    },
    {
      id: 'holiday',
      name: 'Holiday PTO',
      icon: Calendar,
      color: 'amber',
      description: 'High PTO concentration period',
      values: { adc_growth_pct: 0, staff_change: 0, pto_days_next_month: 20, avg_visits_per_patient: 2.5, new_hire_ramp_weeks: 4 }
    },
    {
      id: 'highacuity',
      name: 'High Acuity',
      icon: Heart,
      color: 'purple',
      description: 'Increased visit frequency needs',
      values: { adc_growth_pct: 5, staff_change: 0, pto_days_next_month: 5, avg_visits_per_patient: 3.5, new_hire_ramp_weeks: 4 }
    },
    {
      id: 'newhires',
      name: 'New Hire Wave',
      icon: UserPlus,
      color: 'blue',
      description: 'Multiple new hires onboarding',
      values: { adc_growth_pct: 10, staff_change: 3, pto_days_next_month: 0, avg_visits_per_patient: 2.5, new_hire_ramp_weeks: 6 }
    },
    {
      id: 'worstcase',
      name: 'Worst Case',
      icon: Flame,
      color: 'rose',
      description: 'Growth + attrition + high PTO',
      values: { adc_growth_pct: 20, staff_change: -1, pto_days_next_month: 15, avg_visits_per_patient: 3.0, new_hire_ramp_weeks: 4 }
    }
  ];

  const applyPreset = (preset) => {
    setScenarioInputs(preset.values);
    setSimulationResult(null);
    toast.success(`"${preset.name}" preset applied`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
      </div>
    );
  }

  const { agency, rncm_analytics, hiring_prediction, prn_utilization, ai_insights, workforce_stress_index } = dashboardData || {};

  // Get unique teams from RNCM data
  const getUniqueTeams = () => {
    if (!rncm_analytics) return [];
    const teams = new Set();
    rncm_analytics.forEach(rncm => {
      // Check for team field (from new data) or region field (legacy)
      const team = rncm.team || rncm.region;
      if (team) teams.add(team);
    });
    return Array.from(teams).sort();
  };

  const uniqueTeams = getUniqueTeams();

  // Filter RNCM analytics by selected team
  const filteredRncmAnalytics = selectedTeam === 'all' 
    ? rncm_analytics 
    : rncm_analytics?.filter(rncm => {
        const team = rncm.team || rncm.region;
        return team === selectedTeam;
      });

  // Team color mapping
  const getTeamColor = (team) => {
    const colors = {
      'BLUE': 'bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200',
      'GREEN': 'bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200',
      'YELLOW': 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200',
      'WOUND RN': 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200'
    };
    return colors[team] || 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200';
  };

  const getTeamBadgeColor = (team) => {
    const colors = {
      'BLUE': 'bg-blue-500',
      'GREEN': 'bg-emerald-500',
      'YELLOW': 'bg-amber-500',
      'WOUND RN': 'bg-purple-500'
    };
    return colors[team] || 'bg-slate-500';
  };

  // Export Functions
  const generateTeamReportData = () => {
    if (!rncm_analytics || !uniqueTeams.length) return null;
    
    const reportDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    });
    
    const teamData = uniqueTeams.map(team => {
      const teamMembers = rncm_analytics.filter(r => (r.team || r.region) === team);
      const avgVisits = teamMembers.length > 0 
        ? (teamMembers.reduce((sum, r) => sum + (r.productivity?.weekly_visits || 0), 0) / teamMembers.length).toFixed(1)
        : 0;
      const avgCapacity = teamMembers.length > 0
        ? (teamMembers.reduce((sum, r) => sum + (r.capacity?.capacity_score || 0), 0) / teamMembers.length * 100).toFixed(0)
        : 0;
      const avgProductivity = teamMembers.length > 0
        ? (teamMembers.reduce((sum, r) => sum + (r.productivity?.productivity_ratio || 0), 0) / teamMembers.length * 100).toFixed(0)
        : 0;
      const avgBurnout = teamMembers.length > 0
        ? Math.round(teamMembers.reduce((sum, r) => sum + (r.burnout?.burnout_score || 0), 0) / teamMembers.length)
        : 0;
      const highRisk = teamMembers.filter(r => r.burnout?.risk_tier === 'High').length;
      const moderateRisk = teamMembers.filter(r => r.burnout?.risk_tier === 'Moderate').length;
      const lowRisk = teamMembers.filter(r => r.burnout?.risk_tier === 'Low').length;
      const healthScore = teamMembers.length > 0
        ? Math.round(((lowRisk * 100 + moderateRisk * 50 + highRisk * 0) / teamMembers.length))
        : 100;
      
      return {
        team,
        staffCount: teamMembers.length,
        avgVisits,
        avgCapacity,
        avgProductivity,
        avgBurnout,
        highRisk,
        moderateRisk,
        lowRisk,
        healthScore,
        members: teamMembers.map(m => ({
          name: m.name,
          hireDate: m.hire_date || 'N/A',
          visits: m.productivity?.weekly_visits || 0,
          productivity: ((m.productivity?.productivity_ratio || 0) * 100).toFixed(0),
          burnoutScore: m.burnout?.burnout_score || 0,
          riskTier: m.burnout?.risk_tier || 'Unknown'
        }))
      };
    });
    
    const totals = {
      totalStaff: rncm_analytics.length,
      avgVisits: (rncm_analytics.reduce((sum, r) => sum + (r.productivity?.weekly_visits || 0), 0) / rncm_analytics.length).toFixed(1),
      avgCapacity: (rncm_analytics.reduce((sum, r) => sum + (r.capacity?.capacity_score || 0), 0) / rncm_analytics.length * 100).toFixed(0),
      avgProductivity: (rncm_analytics.reduce((sum, r) => sum + (r.productivity?.productivity_ratio || 0), 0) / rncm_analytics.length * 100).toFixed(0),
      highRisk: rncm_analytics.filter(r => r.burnout?.risk_tier === 'High').length,
      moderateRisk: rncm_analytics.filter(r => r.burnout?.risk_tier === 'Moderate').length,
      lowRisk: rncm_analytics.filter(r => r.burnout?.risk_tier === 'Low').length,
      healthScore: Math.round(((rncm_analytics.filter(r => r.burnout?.risk_tier === 'Low').length * 100 + 
        rncm_analytics.filter(r => r.burnout?.risk_tier === 'Moderate').length * 50) / rncm_analytics.length))
    };
    
    return { reportDate, teamData, totals, adc: agency?.adc || 178 };
  };

  const exportToCSV = () => {
    const data = generateTeamReportData();
    if (!data) {
      toast.error('No data available to export');
      return;
    }
    
    let csv = 'Hospice CareLens - Team Workforce Report\n';
    csv += `Generated: ${data.reportDate}\n`;
    csv += `Average Daily Census: ${data.adc}\n\n`;
    
    // Team Summary
    csv += 'TEAM SUMMARY\n';
    csv += 'Team,Staff,Avg Visits/Wk,Avg Capacity %,Productivity %,High Risk,Moderate Risk,Low Risk,Health Score\n';
    data.teamData.forEach(t => {
      csv += `${t.team},${t.staffCount},${t.avgVisits},${t.avgCapacity},${t.avgProductivity},${t.highRisk},${t.moderateRisk},${t.lowRisk},${t.healthScore}/100\n`;
    });
    csv += `ALL TEAMS,${data.totals.totalStaff},${data.totals.avgVisits},${data.totals.avgCapacity},${data.totals.avgProductivity},${data.totals.highRisk},${data.totals.moderateRisk},${data.totals.lowRisk},${data.totals.healthScore}/100\n\n`;
    
    // Individual Clinicians by Team
    csv += 'INDIVIDUAL CLINICIANS\n';
    csv += 'Team,Name,Hire Date,Weekly Visits,Productivity %,Burnout Score,Risk Level\n';
    data.teamData.forEach(t => {
      t.members.forEach(m => {
        csv += `${t.team},"${m.name}",${m.hireDate},${m.visits},${m.productivity},${m.burnoutScore},${m.riskTier}\n`;
      });
    });
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `team_workforce_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('CSV report downloaded!');
  };

  const exportToPDF = async () => {
    const data = generateTeamReportData();
    if (!data) {
      toast.error('No data available to export');
      return;
    }
    
    try {
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(79, 70, 229); // Indigo
      doc.rect(0, 0, pageWidth, 35, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Hospice CareLens', 14, 18);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Team Workforce Intelligence Report', 14, 28);
      
      // Report Info
      doc.setFontSize(10);
      doc.text(`Generated: ${data.reportDate}`, pageWidth - 14, 18, { align: 'right' });
      doc.text(`ADC: ${data.adc} patients`, pageWidth - 14, 26, { align: 'right' });
      
      let yPos = 45;
      
      // Executive Summary
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Executive Summary', 14, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      // Summary box
      doc.setFillColor(249, 250, 251);
      doc.roundedRect(14, yPos - 3, pageWidth - 28, 35, 3, 3, 'F');
      
      const summaryItems = [
        ['Total Staff:', data.totals.totalStaff],
        ['Health Score:', `${data.totals.healthScore}/100`],
        ['High Risk:', data.totals.highRisk],
        ['Avg Productivity:', `${data.totals.avgProductivity}%`],
        ['Avg Visits/Week:', data.totals.avgVisits]
      ];
      
      let col1X = 20, col2X = 70, col3X = 120;
      doc.setFont('helvetica', 'bold');
      doc.text(summaryItems[0][0], col1X, yPos + 5);
      doc.text(summaryItems[1][0], col1X, yPos + 12);
      doc.text(summaryItems[2][0], col2X, yPos + 5);
      doc.text(summaryItems[3][0], col2X, yPos + 12);
      doc.text(summaryItems[4][0], col3X, yPos + 5);
      
      doc.setFont('helvetica', 'normal');
      doc.text(String(summaryItems[0][1]), col1X + 30, yPos + 5);
      doc.text(String(summaryItems[1][1]), col1X + 35, yPos + 12);
      doc.text(String(summaryItems[2][1]), col2X + 25, yPos + 5);
      doc.text(String(summaryItems[3][1]), col2X + 40, yPos + 12);
      doc.text(String(summaryItems[4][1]), col3X + 40, yPos + 5);
      
      yPos += 45;
      
      // Team Comparison Table Header
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Team Comparison', 14, yPos);
      yPos += 10;
      
      // Draw table
      doc.setFontSize(8);
      const headers = ['Team', 'Staff', 'Visits/Wk', 'Capacity', 'Productivity', 'High Risk', 'Moderate', 'Low Risk', 'Health'];
      const colWidths = [28, 16, 22, 22, 26, 22, 22, 22, 22];
      let xPos = 14;
      
      // Header row
      doc.setFillColor(79, 70, 229);
      doc.rect(14, yPos - 4, pageWidth - 28, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      
      headers.forEach((header, idx) => {
        doc.text(header, xPos + 1, yPos);
        xPos += colWidths[idx];
      });
      yPos += 6;
      
      // Data rows
      doc.setTextColor(30, 30, 30);
      doc.setFont('helvetica', 'normal');
      
      data.teamData.forEach((team, rowIdx) => {
        xPos = 14;
        
        // Row background
        if (rowIdx % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(14, yPos - 4, pageWidth - 28, 7, 'F');
        }
        
        const rowData = [
          team.team,
          String(team.staffCount),
          team.avgVisits,
          `${team.avgCapacity}%`,
          `${team.avgProductivity}%`,
          String(team.highRisk),
          String(team.moderateRisk),
          String(team.lowRisk),
          `${team.healthScore}/100`
        ];
        
        doc.setTextColor(30, 30, 30);
        doc.setFont('helvetica', 'bold');
        doc.text(rowData[0], xPos + 1, yPos);
        xPos += colWidths[0];
        
        doc.setFont('helvetica', 'normal');
        for (let i = 1; i < rowData.length; i++) {
          // Color code risk columns
          if (i === 5 && parseInt(rowData[i]) > 0) {
            doc.setTextColor(220, 38, 38);
          } else if (i === 6 && parseInt(rowData[i]) > 0) {
            doc.setTextColor(217, 119, 6);
          } else if (i === 7 && parseInt(rowData[i]) > 0) {
            doc.setTextColor(22, 163, 74);
          } else {
            doc.setTextColor(30, 30, 30);
          }
          doc.text(rowData[i], xPos + 1, yPos);
          xPos += colWidths[i];
        }
        yPos += 7;
      });
      
      // Totals row
      xPos = 14;
      doc.setFillColor(229, 231, 235);
      doc.rect(14, yPos - 4, pageWidth - 28, 7, 'F');
      doc.setTextColor(30, 30, 30);
      doc.setFont('helvetica', 'bold');
      
      const totalsRow = [
        'ALL TEAMS',
        String(data.totals.totalStaff),
        data.totals.avgVisits,
        `${data.totals.avgCapacity}%`,
        `${data.totals.avgProductivity}%`,
        String(data.totals.highRisk),
        String(data.totals.moderateRisk),
        String(data.totals.lowRisk),
        `${data.totals.healthScore}/100`
      ];
      
      totalsRow.forEach((cell, idx) => {
        doc.text(cell, xPos + 1, yPos);
        xPos += colWidths[idx];
      });
      yPos += 15;
      
      // Clinician Details
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('Clinician Details by Team', 14, yPos);
      yPos += 10;
      
      data.teamData.forEach((team) => {
        if (yPos > 260) {
          doc.addPage();
          yPos = 20;
        }
        
        // Team header
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(79, 70, 229);
        doc.text(`${team.team} Team (${team.staffCount} staff)`, 14, yPos);
        yPos += 7;
        
        // Team member list
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        
        team.members.forEach((member) => {
          if (yPos > 275) {
            doc.addPage();
            yPos = 20;
          }
          
          doc.setTextColor(60, 60, 60);
          doc.text(`${member.name}`, 18, yPos);
          doc.text(`Hired: ${member.hireDate}`, 78, yPos);
          doc.text(`Visits: ${member.visits}`, 115, yPos);
          doc.text(`Prod: ${member.productivity}%`, 140, yPos);
          
          // Risk level with color
          if (member.riskTier === 'High') {
            doc.setTextColor(220, 38, 38);
          } else if (member.riskTier === 'Moderate') {
            doc.setTextColor(217, 119, 6);
          } else {
            doc.setTextColor(22, 163, 74);
          }
          doc.text(`${member.riskTier} Risk`, 168, yPos);
          
          yPos += 5;
        });
        yPos += 5;
      });
      
      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Page ${i} of ${pageCount} | Hospice CareLens - Confidential`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
      
      // Save
      doc.save(`team_workforce_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF report downloaded!');
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div className="space-y-6" data-testid="workforce-intelligence-page">
      {/* Modern Header */}
      <PageHeader
        icon={Brain}
        title="Workforce Intelligence Engine"
        subtitle="AI-powered RNCM productivity, capacity & workforce prediction"
        gradient="from-indigo-600 via-blue-600 to-cyan-600"
      >
        <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={() => { fetchDashboardData(); fetchAlerts(); }} data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </PageHeader>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <ModernCard className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-red-600" />
              <div className="flex-1">
                <p className="font-medium text-red-800 dark:text-red-400">
                  {alerts.length} Active Alert{alerts.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600 dark:text-red-500">
                  {alerts[0]?.message}
                </p>
              </div>
              <Button size="sm" variant="destructive" onClick={() => setActiveTab('alerts')} data-testid="view-alerts-btn">
                View All
              </Button>
            </div>
          </CardContent>
        </ModernCard>
      )}

      {/* Data Sync Status */}
      <DataSyncStatus showFixButton={true} />

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Workforce Stress Index */}
        <Card className={`border ${getStatusBg(workforce_stress_index?.color)}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Workforce Stress Index</p>
                <p className="text-2xl font-bold">{workforce_stress_index?.score || 0}</p>
                <Badge className={getStatusColor(workforce_stress_index?.color)}>
                  {workforce_stress_index?.level || 'Unknown'}
                </Badge>
              </div>
              <Gauge className={`h-10 w-10 ${
                workforce_stress_index?.color === 'red' ? 'text-red-500' :
                workforce_stress_index?.color === 'yellow' ? 'text-amber-500' : 'text-emerald-500'
              }`} />
            </div>
          </CardContent>
        </Card>

        {/* ADC per RNCM */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">ADC per RNCM</p>
                <p className="text-2xl font-bold">
                  {agency?.total_rncm_headcount ? 
                    (agency.adc / agency.total_rncm_headcount).toFixed(1) : 'N/A'}
                </p>
                <p className="text-xs text-slate-400">Target: 15</p>
              </div>
              <Users className="h-10 w-10 text-[#00b0ab]" />
            </div>
          </CardContent>
        </Card>

        {/* Hiring Status */}
        <Card className={`border ${getStatusBg(hiring_prediction?.urgency === 'critical' ? 'red' : hiring_prediction?.urgency === 'high' ? 'yellow' : 'green')}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Hiring Urgency</p>
                <p className="text-2xl font-bold">{hiring_prediction?.time_to_hire_trigger || 'Monitor'}</p>
                <Badge className={getStatusColor(
                  hiring_prediction?.urgency === 'critical' ? 'red' : 
                  hiring_prediction?.urgency === 'high' ? 'yellow' : 'green'
                )}>
                  Gap: {hiring_prediction?.hiring_gap?.toFixed(1) || 0} RNCMs
                </Badge>
              </div>
              <UserPlus className="h-10 w-10 text-[#00b0ab]" />
            </div>
          </CardContent>
        </Card>

        {/* PRN Utilization */}
        <Card className={`border ${prn_utilization?.cost_leak_risk ? 'border-amber-200 bg-amber-50' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">PRN Utilization</p>
                <p className="text-2xl font-bold">{prn_utilization?.current_prn_pct || 0}%</p>
                {prn_utilization?.cost_leak_risk && (
                  <Badge className="bg-amber-100 text-amber-700">Above threshold</Badge>
                )}
              </div>
              <Activity className="h-10 w-10 text-[#00b0ab]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-8 w-full max-w-5xl">
          <TabsTrigger value="report" data-testid="tab-report">
            <FileText className="h-4 w-4 mr-1" /> Report
          </TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <BarChart3 className="h-4 w-4 mr-1" /> Overview
          </TabsTrigger>
          <TabsTrigger value="rncm" data-testid="tab-rncm">
            <Users className="h-4 w-4 mr-1" /> RNCMs
          </TabsTrigger>
          <TabsTrigger value="predictions" data-testid="tab-predictions">
            <TrendingUp className="h-4 w-4 mr-1" /> Predictions
          </TabsTrigger>
          <TabsTrigger value="simulator" data-testid="tab-simulator">
            <Sliders className="h-4 w-4 mr-1" /> Simulator
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">
            <Brain className="h-4 w-4 mr-1" /> AI Insights
          </TabsTrigger>
          <TabsTrigger value="burnout" data-testid="tab-burnout">
            <Flame className="h-4 w-4 mr-1" /> Burnout
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <Bell className="h-4 w-4 mr-1" /> Alerts
          </TabsTrigger>
        </TabsList>

        {/* Enhanced Report Tab */}
        <TabsContent value="report" className="space-y-4">
          <EnhancedReportSection 
            domain="workforce"
            title="Workforce Intelligence Report"
            description="Staff utilization, burnout risk, and capacity metrics with AI insights"
            defaultTimeframe="30d"
          />
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Agency Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[#00b0ab]" />
                Agency Overview
              </CardTitle>
              <CardDescription>
                Real-time metrics from primary data sources
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Average Daily Census</p>
                  <p className="text-xl font-bold">{agency?.adc || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Total RNCMs</p>
                  <p className="text-xl font-bold">{agency?.total_rncm_headcount || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Weekly Admissions</p>
                  <p className="text-xl font-bold">{agency?.weekly_admissions || 0}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Live Discharges</p>
                  <p className="text-xl font-bold">{agency?.live_discharges || 0}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-3 rounded-lg ${(agency?.staff_utilization || 0) > 90 ? 'bg-red-50 border border-red-200' : (agency?.staff_utilization || 0) > 75 ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <p className="text-sm text-slate-500">Staff Utilization</p>
                  <p className={`text-xl font-bold ${(agency?.staff_utilization || 0) > 90 ? 'text-red-600' : (agency?.staff_utilization || 0) > 75 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {agency?.staff_utilization || 0}%
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${(agency?.over_capacity_count || 0) > 0 ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <p className="text-sm text-slate-500">Over Capacity</p>
                  <p className={`text-xl font-bold ${(agency?.over_capacity_count || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {agency?.over_capacity_count || 0} staff
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">PRN Utilization</p>
                  <p className="text-xl font-bold">{agency?.prn_utilization_pct || 0}%</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Hospitalization Rate</p>
                  <p className="text-xl font-bold">{agency?.hospitalization_rate || 0}%</p>
                </div>
              </div>
              {dashboardData?.data_sources && (
                <div className="mt-4 pt-4 border-t text-xs text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  Sources: Census ({dashboardData.data_sources.census}), Workforce ({dashboardData.data_sources.rncm})
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Summary */}
          {ai_insights?.summary && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-[#00b0ab]" />
                  Intelligence Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-[#00b0ab]/10 rounded-lg border border-[#00b0ab]/30">
                    <p className="text-sm text-[#00b0ab]">Total RNCMs Tracked</p>
                    <p className="text-xl font-bold text-indigo-800">{ai_insights.summary.total_rncms}</p>
                  </div>
                  <div className={`p-3 rounded-lg border ${ai_insights.summary.high_burnout_risk > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <p className={`text-sm ${ai_insights.summary.high_burnout_risk > 0 ? 'text-red-600' : 'text-emerald-600'}`}>High Burnout Risk</p>
                    <p className={`text-xl font-bold ${ai_insights.summary.high_burnout_risk > 0 ? 'text-red-800' : 'text-emerald-800'}`}>{ai_insights.summary.high_burnout_risk}</p>
                  </div>
                  <div className={`p-3 rounded-lg border ${ai_insights.summary.at_capacity > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    <p className={`text-sm ${ai_insights.summary.at_capacity > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>At/Over Capacity</p>
                    <p className={`text-xl font-bold ${ai_insights.summary.at_capacity > 0 ? 'text-amber-800' : 'text-emerald-800'}`}>{ai_insights.summary.at_capacity}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600">Avg Capacity Score</p>
                    <p className="text-xl font-bold text-slate-800">{ai_insights.summary.average_capacity_score}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* RNCM Tab */}
        <TabsContent value="rncm" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#00b0ab]" />
                    RNCM Performance Dashboard
                  </CardTitle>
                  <CardDescription>
                    Individual productivity, capacity, and burnout metrics
                  </CardDescription>
                </div>
                
                {/* Team Filter */}
                {uniqueTeams.length > 0 && (
                  <div className="flex items-center gap-2" data-testid="team-filter">
                    <Filter className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-500">Filter by Team:</span>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant={selectedTeam === 'all' ? 'default' : 'outline'}
                        onClick={() => setSelectedTeam('all')}
                        className={`h-7 px-3 text-xs ${selectedTeam === 'all' ? 'bg-indigo-600' : ''}`}
                        data-testid="filter-all"
                      >
                        All ({rncm_analytics?.length || 0})
                      </Button>
                      {uniqueTeams.map((team) => {
                        const teamCount = rncm_analytics?.filter(r => (r.team || r.region) === team).length || 0;
                        return (
                          <Button
                            key={team}
                            size="sm"
                            variant={selectedTeam === team ? 'default' : 'outline'}
                            onClick={() => setSelectedTeam(team)}
                            className={`h-7 px-3 text-xs ${
                              selectedTeam === team 
                                ? getTeamBadgeColor(team) + ' text-white border-0' 
                                : getTeamColor(team)
                            }`}
                            data-testid={`filter-${team.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {team} ({teamCount})
                          </Button>
                        );
                      })}
                      {selectedTeam !== 'all' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedTeam('all')}
                          className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
                          data-testid="clear-filter"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Team Comparison View */}
              {selectedTeam === 'all' && uniqueTeams.length > 0 && (
                <div className="mb-6" data-testid="team-comparison">
                  {/* Team Comparison Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-[#00b0ab]" />
                      <h3 className="font-semibold text-slate-800">Team Comparison</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 mr-2">Export Report:</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={exportToCSV}
                        className="h-8 px-3 text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="export-csv"
                      >
                        <FileSpreadsheet className="h-3.5 w-3.5" />
                        CSV
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={exportToPDF}
                        className="h-8 px-3 text-xs gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
                        data-testid="export-pdf"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        PDF
                      </Button>
                    </div>
                  </div>
                  
                  {/* Comparison Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 font-semibold text-slate-600">Team</th>
                          <th className="text-center p-3 font-semibold text-slate-600">Staff</th>
                          <th className="text-center p-3 font-semibold text-slate-600">Avg Visits/Wk</th>
                          <th className="text-center p-3 font-semibold text-slate-600">Avg Capacity</th>
                          <th className="text-center p-3 font-semibold text-slate-600">Productivity</th>
                          <th className="text-center p-3 font-semibold text-slate-600">
                            <span className="flex items-center justify-center gap-1">
                              <Flame className="h-3.5 w-3.5 text-orange-500" />
                              High Risk
                            </span>
                          </th>
                          <th className="text-center p-3 font-semibold text-slate-600">
                            <span className="flex items-center justify-center gap-1">
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                              Moderate
                            </span>
                          </th>
                          <th className="text-center p-3 font-semibold text-slate-600">
                            <span className="flex items-center justify-center gap-1">
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                              Low Risk
                            </span>
                          </th>
                          <th className="text-center p-3 font-semibold text-slate-600">Health Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uniqueTeams.map((team) => {
                          const teamMembers = rncm_analytics?.filter(r => (r.team || r.region) === team) || [];
                          const avgVisits = teamMembers.length > 0 
                            ? (teamMembers.reduce((sum, r) => sum + (r.productivity?.weekly_visits || 0), 0) / teamMembers.length).toFixed(1)
                            : 0;
                          const avgCapacity = teamMembers.length > 0
                            ? (teamMembers.reduce((sum, r) => sum + (r.capacity?.capacity_score || 0), 0) / teamMembers.length * 100).toFixed(0)
                            : 0;
                          const avgProductivity = teamMembers.length > 0
                            ? (teamMembers.reduce((sum, r) => sum + (r.productivity?.productivity_ratio || 0), 0) / teamMembers.length * 100).toFixed(0)
                            : 0;
                          const highRisk = teamMembers.filter(r => r.burnout?.risk_tier === 'High').length;
                          const moderateRisk = teamMembers.filter(r => r.burnout?.risk_tier === 'Moderate').length;
                          const lowRisk = teamMembers.filter(r => r.burnout?.risk_tier === 'Low').length;
                          
                          // Calculate health score (0-100)
                          const healthScore = teamMembers.length > 0
                            ? Math.round(((lowRisk * 100 + moderateRisk * 50 + highRisk * 0) / teamMembers.length))
                            : 100;
                          
                          const getHealthColor = (score) => {
                            if (score >= 80) return 'text-emerald-600 bg-emerald-100';
                            if (score >= 60) return 'text-amber-600 bg-amber-100';
                            return 'text-red-600 bg-red-100';
                          };
                          
                          return (
                            <tr 
                              key={team}
                              className={`border-b cursor-pointer transition-all hover:bg-slate-50 ${getTeamColor(team).split(' ')[0]}`}
                              onClick={() => setSelectedTeam(team)}
                              data-testid={`team-row-${team.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <div className={`w-3 h-3 rounded-full ${getTeamBadgeColor(team)}`} />
                                  <span className="font-semibold">{team}</span>
                                </div>
                              </td>
                              <td className="text-center p-3">
                                <span className="font-medium">{teamMembers.length}</span>
                              </td>
                              <td className="text-center p-3">
                                <span className="font-medium">{avgVisits}</span>
                              </td>
                              <td className="text-center p-3">
                                <div className="flex items-center justify-center gap-2">
                                  <Progress value={parseFloat(avgCapacity)} className="w-16 h-2" />
                                  <span className="font-medium text-xs">{avgCapacity}%</span>
                                </div>
                              </td>
                              <td className="text-center p-3">
                                <Badge variant="outline" className={`${
                                  parseFloat(avgProductivity) >= 90 ? 'border-emerald-500 text-emerald-600' :
                                  parseFloat(avgProductivity) >= 70 ? 'border-amber-500 text-amber-600' : 'border-red-500 text-red-600'
                                }`}>
                                  {avgProductivity}%
                                </Badge>
                              </td>
                              <td className="text-center p-3">
                                {highRisk > 0 ? (
                                  <Badge className="bg-red-100 text-red-700 border-red-300">
                                    {highRisk}
                                  </Badge>
                                ) : (
                                  <span className="text-slate-300">-</span>
                                )}
                              </td>
                              <td className="text-center p-3">
                                {moderateRisk > 0 ? (
                                  <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                                    {moderateRisk}
                                  </Badge>
                                ) : (
                                  <span className="text-slate-300">-</span>
                                )}
                              </td>
                              <td className="text-center p-3">
                                {lowRisk > 0 ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                                    {lowRisk}
                                  </Badge>
                                ) : (
                                  <span className="text-slate-300">-</span>
                                )}
                              </td>
                              <td className="text-center p-3">
                                <Badge className={`${getHealthColor(healthScore)} font-semibold`}>
                                  {healthScore}/100
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Total Row */}
                        <tr className="bg-slate-100 font-semibold">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-slate-600" />
                              <span>All Teams</span>
                            </div>
                          </td>
                          <td className="text-center p-3">{rncm_analytics?.length || 0}</td>
                          <td className="text-center p-3">
                            {rncm_analytics?.length > 0 
                              ? (rncm_analytics.reduce((sum, r) => sum + (r.productivity?.weekly_visits || 0), 0) / rncm_analytics.length).toFixed(1)
                              : 0}
                          </td>
                          <td className="text-center p-3">
                            <div className="flex items-center justify-center gap-2">
                              <Progress 
                                value={rncm_analytics?.length > 0 
                                  ? (rncm_analytics.reduce((sum, r) => sum + (r.capacity?.capacity_score || 0), 0) / rncm_analytics.length * 100)
                                  : 0} 
                                className="w-16 h-2" 
                              />
                              <span className="text-xs">
                                {rncm_analytics?.length > 0 
                                  ? (rncm_analytics.reduce((sum, r) => sum + (r.capacity?.capacity_score || 0), 0) / rncm_analytics.length * 100).toFixed(0)
                                  : 0}%
                              </span>
                            </div>
                          </td>
                          <td className="text-center p-3">
                            <Badge variant="outline" className="border-[#00b0ab] text-[#00b0ab]">
                              {rncm_analytics?.length > 0 
                                ? (rncm_analytics.reduce((sum, r) => sum + (r.productivity?.productivity_ratio || 0), 0) / rncm_analytics.length * 100).toFixed(0)
                                : 0}%
                            </Badge>
                          </td>
                          <td className="text-center p-3">
                            {rncm_analytics?.filter(r => r.burnout?.risk_tier === 'High').length || 0}
                          </td>
                          <td className="text-center p-3">
                            {rncm_analytics?.filter(r => r.burnout?.risk_tier === 'Moderate').length || 0}
                          </td>
                          <td className="text-center p-3">
                            {rncm_analytics?.filter(r => r.burnout?.risk_tier === 'Low').length || 0}
                          </td>
                          <td className="text-center p-3">
                            <Badge className="bg-[#00b0ab]/15 text-indigo-700 font-semibold">
                              {rncm_analytics?.length > 0 
                                ? Math.round(((rncm_analytics.filter(r => r.burnout?.risk_tier === 'Low').length * 100 + 
                                    rncm_analytics.filter(r => r.burnout?.risk_tier === 'Moderate').length * 50) / rncm_analytics.length))
                                : 100}/100
                            </Badge>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Quick Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                    {uniqueTeams.map((team) => {
                      const teamMembers = rncm_analytics?.filter(r => (r.team || r.region) === team) || [];
                      const avgBurnout = teamMembers.length > 0
                        ? Math.round(teamMembers.reduce((sum, r) => sum + (r.burnout?.burnout_score || 0), 0) / teamMembers.length)
                        : 0;
                      const highRisk = teamMembers.filter(r => r.burnout?.risk_tier === 'High').length;
                      
                      return (
                        <div 
                          key={`stat-${team}`}
                          className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${getTeamColor(team)}`}
                          onClick={() => setSelectedTeam(team)}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${getTeamBadgeColor(team)}`} />
                            <span className="font-semibold text-sm">{team}</span>
                          </div>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-xs text-slate-500">Avg Burnout</p>
                              <p className={`text-lg font-bold ${avgBurnout > 50 ? 'text-red-600' : avgBurnout > 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {avgBurnout}
                              </p>
                            </div>
                            {highRisk > 0 && (
                              <div className="flex items-center gap-1 text-red-600">
                                <Flame className="h-4 w-4" />
                                <span className="text-sm font-semibold">{highRisk}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Selected Team Header */}
              {selectedTeam !== 'all' && (
                <div className={`p-3 rounded-lg mb-4 flex items-center justify-between ${getTeamColor(selectedTeam)}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getTeamBadgeColor(selectedTeam)}`} />
                    <span className="font-semibold">{selectedTeam} Team</span>
                    <Badge variant="outline" className="ml-2">
                      {filteredRncmAnalytics?.length || 0} clinicians
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedTeam('all')}
                    className="h-7"
                  >
                    Show All Teams
                  </Button>
                </div>
              )}

              {filteredRncmAnalytics && filteredRncmAnalytics.length > 0 ? (
                <div className="space-y-4">
                  {filteredRncmAnalytics.map((rncm, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${getStatusBg(rncm.burnout?.color || 'gray')}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <h3 className="font-semibold text-lg">{rncm.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              {(rncm.team || rncm.region) && (
                                <Badge className={`${getTeamColor(rncm.team || rncm.region)} text-xs`}>
                                  {rncm.team || rncm.region}
                                </Badge>
                              )}
                              {rncm.hire_date && (
                                <span className="text-xs text-slate-400">
                                  Since {rncm.hire_date}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <CapacityIcon status={rncm.capacity?.capacity_status} />
                          <Badge className={getStatusColor(rncm.capacity?.color)}>
                            {rncm.capacity?.capacity_status}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Visits */}
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-slate-500">Weekly Visits</p>
                          <p className="text-lg font-bold">{rncm.productivity?.weekly_visits || 0}</p>
                          <p className="text-xs text-slate-400">Target: {rncm.productivity?.optimal_range}</p>
                        </div>

                        {/* Productivity */}
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-slate-500">Productivity</p>
                          <p className="text-lg font-bold">{(rncm.productivity?.productivity_ratio * 100).toFixed(0)}%</p>
                          <Badge variant="outline" className={`text-xs ${
                            rncm.productivity?.productivity_status === 'Optimal' ? 'border-emerald-500 text-emerald-600' :
                            rncm.productivity?.productivity_status === 'Overextended' ? 'border-red-500 text-red-600' : 'border-amber-500 text-amber-600'
                          }`}>
                            {rncm.productivity?.productivity_status}
                          </Badge>
                        </div>

                        {/* Capacity Score */}
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-slate-500">Capacity Score</p>
                          <p className="text-lg font-bold">{rncm.capacity?.capacity_score}</p>
                          <Progress 
                            value={Math.min(rncm.capacity?.capacity_score * 100, 100)} 
                            className="h-1.5 mt-1"
                          />
                        </div>

                        {/* Burnout Risk */}
                        <div className={`bg-white p-3 rounded border ${rncm.burnout?.risk_tier === 'High' ? 'border-red-300' : ''}`}>
                          <p className="text-xs text-slate-500">Burnout Score</p>
                          <div className="flex items-center gap-2">
                            <p className="text-lg font-bold">{rncm.burnout?.burnout_score || 0}</p>
                            {rncm.burnout?.risk_tier === 'High' && <Flame className="h-4 w-4 text-red-500" />}
                          </div>
                          <Badge className={getStatusColor(rncm.burnout?.color)}>
                            {rncm.burnout?.risk_tier} Risk
                          </Badge>
                        </div>
                      </div>

                      {/* Burnout Factors */}
                      {rncm.burnout?.burnout_factors?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs font-medium text-slate-600 mb-1">Risk Factors:</p>
                          <div className="flex flex-wrap gap-1">
                            {rncm.burnout.burnout_factors.map((factor, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-white">
                                {factor}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">
                    {selectedTeam !== 'all' 
                      ? `No clinicians found in ${selectedTeam} team` 
                      : 'No RNCM data available'}
                  </p>
                  <p className="text-sm text-slate-400">
                    {selectedTeam !== 'all' 
                      ? 'Try selecting a different team or show all'
                      : 'Add RNCM capacity entries to see analytics'}
                  </p>
                  {selectedTeam !== 'all' && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setSelectedTeam('all')}
                    >
                      Show All Teams
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions" className="space-y-4">
          {/* Hiring Prediction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-[#00b0ab]" />
                Hiring Prediction Model
              </CardTitle>
              <CardDescription>4-week workforce projection based on admission trends</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Admission Growth Rate</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">{hiring_prediction?.admission_growth_rate || 0}%</p>
                    {hiring_prediction?.admission_growth_rate > 0 ? 
                      <ArrowUpRight className="h-5 w-5 text-emerald-500" /> : 
                      <Minus className="h-5 w-5 text-slate-400" />
                    }
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Projected ADC (4 weeks)</p>
                  <p className="text-2xl font-bold">{hiring_prediction?.projected_adc_4weeks || 0}</p>
                  <p className="text-xs text-slate-400">Current: {hiring_prediction?.current_adc}</p>
                </div>
                <div className={`p-4 rounded-lg ${
                  hiring_prediction?.urgency === 'critical' ? 'bg-red-50 border border-red-200' :
                  hiring_prediction?.urgency === 'high' ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'
                }`}>
                  <p className="text-sm text-slate-500">Hiring Gap</p>
                  <p className="text-2xl font-bold">{hiring_prediction?.hiring_gap?.toFixed(1) || 0} RNCMs</p>
                  <Badge className={getStatusColor(
                    hiring_prediction?.urgency === 'critical' ? 'red' :
                    hiring_prediction?.urgency === 'high' ? 'yellow' : 'green'
                  )}>
                    {hiring_prediction?.time_to_hire_trigger}
                  </Badge>
                </div>
              </div>

              {hiring_prediction?.recommendation && (
                <div className="mt-4 p-4 bg-[#00b0ab]/10 rounded-lg border border-[#00b0ab]/30">
                  <p className="text-sm font-medium text-indigo-800">
                    <Zap className="h-4 w-4 inline mr-1" />
                    Recommendation
                  </p>
                  <p className="text-indigo-700">{hiring_prediction.recommendation}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* PRN Utilization Prediction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-[#00b0ab]" />
                PRN Utilization Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Current PRN %</p>
                  <p className="text-2xl font-bold">{prn_utilization?.current_prn_pct || 0}%</p>
                  <p className="text-xs text-slate-400">Threshold: {prn_utilization?.cost_leak_threshold}%</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Projected PRN (4 weeks)</p>
                  <p className="text-2xl font-bold">{prn_utilization?.projected_prn_pct_4weeks || 0}%</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Unscheduled Visit Ratio</p>
                  <p className="text-2xl font-bold">{prn_utilization?.unscheduled_ratio || 0}%</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">After-Hours Ratio</p>
                  <p className="text-2xl font-bold">{prn_utilization?.after_hours_ratio || 0}%</p>
                </div>
              </div>

              {prn_utilization?.compensating_for?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-slate-600 mb-2">PRN Compensating For:</p>
                  <div className="flex flex-wrap gap-2">
                    {prn_utilization.compensating_for.map((item, i) => (
                      <Badge key={i} variant="outline">{item}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {prn_utilization?.cost_leak_risk && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    {prn_utilization.alert}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Simulator Tab */}
        <TabsContent value="simulator" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Input Controls */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sliders className="h-5 w-5 text-[#00b0ab]" />
                  Scenario Controls
                </CardTitle>
                <CardDescription>Adjust variables to simulate workforce impact</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Scenario Presets */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Quick Presets</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {scenarioPresets.map((preset) => {
                      const IconComponent = preset.icon;
                      const colorClasses = {
                        emerald: 'hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700',
                        red: 'hover:bg-red-50 hover:border-red-300 hover:text-red-700',
                        amber: 'hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700',
                        purple: 'hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700',
                        blue: 'hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700',
                        rose: 'hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700'
                      };
                      return (
                        <Button
                          key={preset.id}
                          variant="outline"
                          size="sm"
                          onClick={() => applyPreset(preset)}
                          className={`justify-start text-left h-auto py-2 px-3 ${colorClasses[preset.color]}`}
                          data-testid={`preset-${preset.id}`}
                        >
                          <IconComponent className="h-4 w-4 mr-2 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-xs truncate">{preset.name}</p>
                            <p className="text-[10px] text-slate-400 truncate">{preset.description}</p>
                          </div>
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">Custom Adjustments</Label>
                </div>

                {/* ADC Growth */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">ADC Growth</Label>
                    <span className={`text-sm font-bold ${
                      scenarioInputs.adc_growth_pct > 0 ? 'text-emerald-600' : 
                      scenarioInputs.adc_growth_pct < 0 ? 'text-red-600' : 'text-slate-600'
                    }`}>
                      {scenarioInputs.adc_growth_pct > 0 ? '+' : ''}{scenarioInputs.adc_growth_pct}%
                    </span>
                  </div>
                  <Slider
                    value={[scenarioInputs.adc_growth_pct]}
                    onValueChange={(v) => setScenarioInputs({...scenarioInputs, adc_growth_pct: v[0]})}
                    min={-20}
                    max={30}
                    step={1}
                    className="w-full"
                    data-testid="slider-adc-growth"
                  />
                  <p className="text-xs text-slate-400">Expected census change over next 4 weeks</p>
                </div>

                {/* Staff Change */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">Staff Change</Label>
                    <span className={`text-sm font-bold ${
                      scenarioInputs.staff_change > 0 ? 'text-emerald-600' : 
                      scenarioInputs.staff_change < 0 ? 'text-red-600' : 'text-slate-600'
                    }`}>
                      {scenarioInputs.staff_change > 0 ? '+' : ''}{scenarioInputs.staff_change} RNCM
                    </span>
                  </div>
                  <Slider
                    value={[scenarioInputs.staff_change]}
                    onValueChange={(v) => setScenarioInputs({...scenarioInputs, staff_change: v[0]})}
                    min={-3}
                    max={5}
                    step={1}
                    className="w-full"
                    data-testid="slider-staff-change"
                  />
                  <p className="text-xs text-slate-400">Add (+) or remove (-) staff members</p>
                </div>

                {/* PTO Days */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">PTO Days (Next Month)</Label>
                    <span className="text-sm font-bold text-slate-600">{scenarioInputs.pto_days_next_month} days</span>
                  </div>
                  <Slider
                    value={[scenarioInputs.pto_days_next_month]}
                    onValueChange={(v) => setScenarioInputs({...scenarioInputs, pto_days_next_month: v[0]})}
                    min={0}
                    max={30}
                    step={1}
                    className="w-full"
                    data-testid="slider-pto-days"
                  />
                  <p className="text-xs text-slate-400">Total PTO days across all staff</p>
                </div>

                {/* Visit Frequency */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">Avg Visits per Patient</Label>
                    <span className="text-sm font-bold text-slate-600">{scenarioInputs.avg_visits_per_patient.toFixed(1)}/week</span>
                  </div>
                  <Slider
                    value={[scenarioInputs.avg_visits_per_patient * 10]}
                    onValueChange={(v) => setScenarioInputs({...scenarioInputs, avg_visits_per_patient: v[0] / 10})}
                    min={15}
                    max={40}
                    step={1}
                    className="w-full"
                    data-testid="slider-visit-freq"
                  />
                  <p className="text-xs text-slate-400">Expected visit frequency per patient</p>
                </div>

                {/* New Hire Ramp-up */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium">New Hire Ramp-up</Label>
                    <span className="text-sm font-bold text-slate-600">{scenarioInputs.new_hire_ramp_weeks} weeks</span>
                  </div>
                  <Slider
                    value={[scenarioInputs.new_hire_ramp_weeks]}
                    onValueChange={(v) => setScenarioInputs({...scenarioInputs, new_hire_ramp_weeks: v[0]})}
                    min={1}
                    max={8}
                    step={1}
                    className="w-full"
                    data-testid="slider-ramp-weeks"
                  />
                  <p className="text-xs text-slate-400">Weeks until new hire reaches full productivity</p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={runSimulation} 
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    disabled={simulatorLoading}
                    data-testid="run-simulation-btn"
                  >
                    {simulatorLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Run Simulation
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={resetSimulation}
                    data-testid="reset-simulation-btn"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results Panel */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#00b0ab]" />
                  Simulation Results
                </CardTitle>
                <CardDescription>
                  {simulationResult ? 'Compare current state vs. projected outcomes' : 'Adjust scenario inputs and run simulation'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {simulationResult ? (
                  <div className="space-y-6">
                    {/* Overall Impact Banner */}
                    <div className={`p-4 rounded-lg border-2 ${
                      simulationResult.overall_impact?.color === 'green' ? 'bg-emerald-50 border-emerald-300' :
                      simulationResult.overall_impact?.color === 'red' ? 'bg-red-50 border-red-300' :
                      simulationResult.overall_impact?.color === 'yellow' ? 'bg-amber-50 border-amber-300' :
                      'bg-slate-50 border-slate-300'
                    }`}>
                      <div className="flex items-center gap-3">
                        {simulationResult.overall_impact?.color === 'green' ? (
                          <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                        ) : simulationResult.overall_impact?.color === 'red' ? (
                          <AlertTriangle className="h-6 w-6 text-red-600" />
                        ) : (
                          <AlertCircle className="h-6 w-6 text-amber-600" />
                        )}
                        <div>
                          <Badge className={`mb-1 ${
                            simulationResult.overall_impact?.color === 'green' ? 'bg-emerald-100 text-emerald-700' :
                            simulationResult.overall_impact?.color === 'red' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {simulationResult.overall_impact?.status}
                          </Badge>
                          <p className="font-medium">{simulationResult.overall_impact?.summary}</p>
                        </div>
                      </div>
                    </div>

                    {/* Side-by-Side Comparison Cards */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Current State */}
                      <div className="p-4 bg-slate-50 rounded-lg border">
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Clock className="h-4 w-4" /> Current State
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-500">ADC</span>
                            <span className="font-medium">{simulationResult.current_state?.adc}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-500">Staff Count</span>
                            <span className="font-medium">{simulationResult.current_state?.staff_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-500">Visits Needed</span>
                            <span className="font-medium">{simulationResult.current_state?.visits_needed}/wk</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-500">Capacity</span>
                            <span className="font-medium">{simulationResult.current_state?.capacity_utilization}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-500">Burnout Avg</span>
                            <span className="font-medium">{simulationResult.current_state?.burnout_avg}</span>
                          </div>
                        </div>
                      </div>

                      {/* Projected State */}
                      <div className={`p-4 rounded-lg border-2 ${
                        simulationResult.overall_impact?.color === 'green' ? 'bg-emerald-50 border-emerald-200' :
                        simulationResult.overall_impact?.color === 'red' ? 'bg-red-50 border-red-200' :
                        'bg-amber-50 border-amber-200'
                      }`}>
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Target className="h-4 w-4" /> Projected State
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">ADC</span>
                            <div className="flex items-center gap-1">
                              <span className="font-medium">{simulationResult.projected_state?.adc}</span>
                              {simulationResult.changes?.adc_change !== 0 && (
                                <span className={`text-xs ${simulationResult.changes?.adc_change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  ({simulationResult.changes?.adc_change > 0 ? '+' : ''}{simulationResult.changes?.adc_change})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Effective Staff</span>
                            <div className="flex items-center gap-1">
                              <span className="font-medium">{simulationResult.projected_state?.effective_staff}</span>
                              {simulationResult.changes?.staff_effective_change !== 0 && (
                                <span className={`text-xs ${simulationResult.changes?.staff_effective_change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  ({simulationResult.changes?.staff_effective_change > 0 ? '+' : ''}{simulationResult.changes?.staff_effective_change})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-slate-500">Visits Needed</span>
                            <span className="font-medium">{simulationResult.projected_state?.visits_needed}/wk</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Capacity</span>
                            <div className="flex items-center gap-1">
                              <span className={`font-medium ${
                                simulationResult.projected_state?.capacity_utilization > 100 ? 'text-red-600' : ''
                              }`}>{simulationResult.projected_state?.capacity_utilization}%</span>
                              {simulationResult.changes?.capacity_change !== 0 && (
                                simulationResult.changes?.capacity_change > 0 ? 
                                  <ArrowUpRight className="h-3 w-3 text-red-500" /> : 
                                  <ArrowDownRight className="h-3 w-3 text-emerald-500" />
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500">Burnout Risk</span>
                            <div className="flex items-center gap-1">
                              <span className={`font-medium ${
                                simulationResult.projected_state?.burnout_projected > 60 ? 'text-red-600' : ''
                              }`}>{simulationResult.projected_state?.burnout_projected}</span>
                              {simulationResult.changes?.burnout_change !== 0 && (
                                simulationResult.changes?.burnout_change > 0 ? 
                                  <ArrowUpRight className="h-3 w-3 text-red-500" /> : 
                                  <ArrowDownRight className="h-3 w-3 text-emerald-500" />
                              )}
                            </div>
                          </div>
                        </div>
                        {simulationResult.projected_state?.hiring_gap > 0 && (
                          <div className="mt-3 pt-3 border-t border-amber-200">
                            <Badge className="bg-amber-100 text-amber-700">
                              Hiring Gap: {simulationResult.projected_state?.hiring_gap} RNCM(s)
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Timeline Chart */}
                    {simulationResult.timeline && simulationResult.timeline.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold text-slate-700 mb-3">8-Week Projection Timeline</h4>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={simulationResult.timeline}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="week" tickFormatter={(v) => `Wk ${v}`} />
                              <YAxis yAxisId="left" domain={[0, 'auto']} />
                              <YAxis yAxisId="right" orientation="right" domain={[0, 150]} />
                              <Tooltip 
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                labelFormatter={(v) => `Week ${v}`}
                              />
                              <Legend />
                              <ReferenceLine yAxisId="right" y={100} stroke="#ef4444" strokeDasharray="5 5" label="100% Capacity" />
                              <Area 
                                yAxisId="right" 
                                type="monotone" 
                                dataKey="capacity_utilization" 
                                stroke="#6366f1" 
                                fill="#c7d2fe" 
                                name="Capacity %" 
                              />
                              <Line 
                                yAxisId="left" 
                                type="monotone" 
                                dataKey="effective_staff" 
                                stroke="#10b981" 
                                strokeWidth={2}
                                name="Effective Staff" 
                              />
                              <Line 
                                yAxisId="left" 
                                type="monotone" 
                                dataKey="projected_burnout" 
                                stroke="#f59e0b" 
                                strokeWidth={2}
                                name="Burnout Score" 
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Risks */}
                    {simulationResult.risks && simulationResult.risks.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold text-slate-700 mb-2">Identified Risks</h4>
                        <div className="space-y-2">
                          {simulationResult.risks.map((risk, idx) => (
                            <div key={idx} className={`p-3 rounded-lg border ${
                              risk.type === 'CRITICAL' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                            }`}>
                              <div className="flex items-start gap-2">
                                {risk.type === 'CRITICAL' ? 
                                  <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" /> : 
                                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                                }
                                <div>
                                  <p className="font-medium text-sm">{risk.message}</p>
                                  <p className="text-xs text-slate-600">{risk.action}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {simulationResult.recommendations && simulationResult.recommendations.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold text-slate-700 mb-2">Recommendations</h4>
                        <div className="space-y-1">
                          {simulationResult.recommendations.map((rec, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-[#00b0ab]/10 rounded">
                              <Zap className="h-4 w-4 text-[#00b0ab] mt-0.5" />
                              <p className="text-sm text-indigo-800">{rec}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Sliders className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500 font-medium">No Simulation Running</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Adjust the scenario controls and click "Run Simulation" to see projected outcomes
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="insights" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-[#00b0ab]" />
                AI-Powered Workforce Insights
              </CardTitle>
              <CardDescription>Predictive intelligence and recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              {ai_insights?.insights?.length > 0 ? (
                <div className="space-y-3">
                  {ai_insights.insights.map((insight, idx) => (
                    <div key={idx} className="p-4 bg-[#00b0ab]/10 rounded-lg border border-[#00b0ab]/30">
                      <div className="flex items-start gap-3">
                        <Zap className="h-5 w-5 text-[#00b0ab] mt-0.5" />
                        <p className="text-indigo-800">{insight}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Brain className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No insights available</p>
                  <p className="text-sm text-slate-400">Add more RNCM data to generate AI insights</p>
                </div>
              )}

              {/* AI Alerts */}
              {ai_insights?.alerts?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold mb-3">AI-Detected Alerts</h3>
                  <div className="space-y-2">
                    {ai_insights.alerts.map((alert, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        alert.type === 'CRITICAL' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          <Badge className={alert.type === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                            {alert.type}
                          </Badge>
                          <p className="font-medium">{alert.message}</p>
                        </div>
                        {alert.action && (
                          <p className="text-sm text-slate-600 mt-1">
                            <ChevronRight className="h-3 w-3 inline" /> {alert.action}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-[#00b0ab]" />
                AI-Powered Workforce Insights
              </CardTitle>
              <CardDescription>Predictive intelligence and recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              {ai_insights?.insights?.length > 0 ? (
                <div className="space-y-3">
                  {ai_insights.insights.map((insight, idx) => (
                    <div key={idx} className="p-4 bg-[#00b0ab]/10 rounded-lg border border-[#00b0ab]/30">
                      <div className="flex items-start gap-3">
                        <Zap className="h-5 w-5 text-[#00b0ab] mt-0.5" />
                        <p className="text-indigo-800">{insight}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Brain className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No insights available</p>
                  <p className="text-sm text-slate-400">Add more RNCM data to generate AI insights</p>
                </div>
              )}

              {/* AI Alerts */}
              {ai_insights?.alerts?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold mb-3">AI-Detected Alerts</h3>
                  <div className="space-y-2">
                    {ai_insights.alerts.map((alert, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        alert.type === 'CRITICAL' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          <Badge className={alert.type === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                            {alert.type}
                          </Badge>
                          <p className="font-medium">{alert.message}</p>
                        </div>
                        {alert.action && (
                          <p className="text-sm text-slate-600 mt-1">
                            <ChevronRight className="h-3 w-3 inline" /> {alert.action}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Burnout Monitoring Tab */}
        <TabsContent value="burnout" className="space-y-4">
          <BurnoutAlerts token={token} />
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-[#00b0ab]" />
                Smart Alert Triggers
              </CardTitle>
              <CardDescription>
                Automated notifications for workforce concerns
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      alert.severity === 'high' ? 'bg-red-50 border-red-200' :
                      alert.severity === 'medium' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {alert.severity === 'high' ? 
                            <AlertTriangle className="h-5 w-5 text-red-600" /> :
                            <AlertCircle className="h-5 w-5 text-amber-600" />
                          }
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                alert.severity === 'high' ? 'bg-red-100 text-red-700' :
                                alert.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                              }>
                                {alert.type}
                              </Badge>
                              {alert.rncm && <span className="text-sm text-slate-600">{alert.rncm}</span>}
                            </div>
                            <p className="font-medium mt-1">{alert.message}</p>
                            {alert.recommendation && (
                              <p className="text-sm text-slate-600 mt-1">
                                Recommendation: {alert.recommendation}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p className="text-emerald-600 font-medium">All Clear!</p>
                  <p className="text-slate-500">No active workforce alerts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
